import { isAfter } from "date-fns";
import { Map, Set } from "immutable";

import { getAssignmentSpecWordIds } from "@seneca/assignments-service-types";

import { Action } from "common/types/redux";

import {
  getAssignment,
  getAssignmentDueDate,
  quizHasBeenStudied,
  sectionContentHasBeenStudied
} from "seneca-common/features/assignments/features/student-assignments/state/selectors/state/assignments/studentAssignments";
import {
  Assignment,
  ReceivedAssignmentStatus
} from "seneca-common/features/assignments/state";
import {
  assignmentIncludesExamQuestions,
  assignmentIncludesQuizzes,
  getAssignmentSectionIds
} from "seneca-common/features/assignments/state/selectors/state/assignments/assignment";
import makeAssignmentsReducer from "seneca-common/features/assignments/utils/reducers/makeAssignmentsReducer";

import actionTypes from "../../../action-types";
import { receiveAssignmentWithContents as receiveAssignmentAction } from "../../../actions";
import ContentStat from "../../../models/ContentStat";
import QuestionStat from "../../../models/QuestionStat";
import QuizStat from "../../../models/QuizStat";
import QuizModuleStat from "../../../models/QuizStat/QuizModuleStat";
import SectionStat, { SectionStatType } from "../../../models/SectionStat";
import StudentAssignment from "../../../models/StudentAssignment";
import VocabularyWordStat from "../../../models/VocabularyWordStat";
import {
  allContentsCompleted,
  getAssignmentExamQuestionIds,
  getAssignmentQuizIds,
  getHasContentSpec,
  getStudiedQuestionIds,
  getStudiedQuizIds,
  getStudiedSectionIds
} from "../../../selectors/state/assignments/assignment/studentAssignment";
import assignmentReducer from "./assignment";
import assignmentContentsReducer from "./assignmentContents";

export const baseAssignmentsReducer = makeAssignmentsReducer({
  assignmentReducer,
  receiveAssignmentActionCreator: receiveAssignmentAction,
  receiveAssignmentActionType: actionTypes.RECEIVE_ASSIGNMENT,
  receiveAssignmentsActionType: actionTypes.RECEIVE_ASSIGNMENTS,
  deleteAssignmentsActionType: actionTypes.DELETE_ASSIGNMENTS
});

export default function combinedStudentAssignmentReducer(
  state: Map<string, StudentAssignment> = Map<string, StudentAssignment>(),
  action: Action<any>
) {
  return assignmentContentsReducer(
    studentAssignmentReducer(state, action),
    action
  );
}

function studentAssignmentReducer(
  state: Map<string, StudentAssignment> = Map<string, StudentAssignment>(),
  action: Action<any>
) {
  let assignments = baseAssignmentsReducer(
    state as Map<string, Assignment>,
    action
  );

  switch (action.type) {
    case actionTypes.UPDATE_ASSIGNMENT_SECTION_STAT: {
      const {
        assignmentId,
        sectionId,
        contentType,
        contentIds,
        ...sessionStats
      } = action.payload;
      const isStudied = sectionContentHasBeenStudied(
        assignments,
        assignmentId,
        sectionId,
        contentType
      );
      if (isStudied) {
        assignments = assignments.updateIn(
          [assignmentId, "sectionStats", sectionId],
          (stat: any) => getUpdatedSectionStat(stat, sessionStats)
        );
        assignments = assignments.updateIn(
          [assignmentId, "contentStats", sectionId, contentType],
          (stat: any) => {
            if (!stat) return Map();

            return new ContentStat({
              ...(getUpdatedSectionStat(
                stat,
                sessionStats
              ).toJS() as SectionStatType),
              contentIds
            });
          }
        );
      } else {
        const { sessionScore, sessionDuration } = sessionStats;
        const sectionStat = new SectionStat({
          bestScore: sessionScore,
          worstScore: sessionScore,
          averageScore: sessionScore,
          lastScore: sessionScore,
          firstScore: sessionScore,
          sessionsCompleted: 1,
          totalStudyTime: sessionDuration
        });
        assignments = assignments.setIn(
          [assignmentId, "sectionStats", sectionId],
          sectionStat
        );
        assignments = assignments.setIn(
          [assignmentId, "contentStats", sectionId, contentType],
          new ContentStat({
            ...(sectionStat.toJS() as SectionStatType),
            contentIds
          })
        );
      }

      if (
        assignmentIsComplete({
          state: state as any,
          assignmentId,
          sectionComplete: true
        })
      ) {
        assignments = assignments.updateIn(
          [assignmentId],
          (assignment: any) => {
            return assignment.set(
              "status",
              isAfter(
                new Date(),
                new Date(getAssignmentDueDate(assignments, assignmentId))
              )
                ? ReceivedAssignmentStatus.late
                : ReceivedAssignmentStatus.complete
            );
          }
        );
      }

      return assignments;
    }
    case actionTypes.UPDATE_ASSIGNMENT_QUESTION_STAT: {
      const { assignmentId, questionId, ...questionStat } = action.payload;
      assignments = assignments.setIn(
        [assignmentId, "questionStats", questionId],
        new QuestionStat({
          ...questionStat
        })
      );

      if (
        assignmentIsComplete({
          state: state as any,
          assignmentId,
          examQuestionComplete: true
        })
      ) {
        assignments = assignments.updateIn(
          [assignmentId],
          (assignment: any) => {
            return assignment.set(
              "status",
              isAfter(
                new Date(),
                new Date(getAssignmentDueDate(assignments, assignmentId))
              )
                ? ReceivedAssignmentStatus.late
                : ReceivedAssignmentStatus.complete
            );
          }
        );
      }

      return assignments;
    }

    case actionTypes.UPDATE_ASSIGNMENT_VOCABULARY_STAT: {
      const { assignmentId, vocabularyStats } = action.payload;

      assignments = assignments.setIn(
        [assignmentId, "vocabularyStats"],
        Object.entries(vocabularyStats).reduce(
          (acc: any, [wordId, stat]: any) =>
            acc.set(wordId, new VocabularyWordStat(stat)),
          Map()
        )
      );

      if (vocabularyAssignmentIsComplete(assignments.get(assignmentId))) {
        assignments = assignments.updateIn(
          [assignmentId],
          (assignment: any) => {
            return assignment.set(
              "status",
              isAfter(
                new Date(),
                new Date(getAssignmentDueDate(assignments, assignmentId))
              )
                ? ReceivedAssignmentStatus.late
                : ReceivedAssignmentStatus.complete
            );
          }
        );
      }

      return assignments;
    }

    case actionTypes.UPDATE_ASSIGNMENT_QUIZ_STAT: {
      const { assignmentId, quizId, modules, timestamp, sessionDuration } =
        action.payload;

      const beenStudied = quizHasBeenStudied(assignments, assignmentId, quizId);

      if (beenStudied) {
        const { isBestSession, isWorstSession } = getIsBestOrWorstQuizSession({
          assignmentId,
          assignments,
          quizId,
          newSessionModules: modules
        });

        assignments = assignments.updateIn(
          [assignmentId, "quizStats", quizId],
          (stat: any) =>
            getUpdatedQuizStat(stat, {
              timestamp,
              sessionDuration,
              modules,
              bestSession: isBestSession,
              worstSession: isWorstSession
            })
        );
      } else {
        const moduleStatMap = modules.reduce(
          (acc: any, { moduleId, sessionMark }: any) => {
            return acc.set(
              moduleId,
              new QuizModuleStat({
                marksTotal: 1,
                bestModuleMark: sessionMark,
                worstModuleMark: sessionMark,
                averageSessionMark: sessionMark,
                lastSessionMark: sessionMark,
                bestSessionMark: sessionMark,
                worstSessionMark: sessionMark,
                firstSessionMark: sessionMark
              })
            );
          },
          Map()
        );

        assignments = assignments.setIn(
          [assignmentId, "quizStats", quizId],
          new QuizStat({
            marksTotal: modules.length,
            totalStudyTime: sessionDuration,
            sessionsCompleted: 1,
            firstStudied: timestamp,
            lastStudied: timestamp,
            moduleStats: moduleStatMap
          })
        );
      }

      if (
        assignmentIsComplete({
          state: state as any,
          assignmentId,
          quizComplete: true
        })
      ) {
        assignments = assignments.updateIn(
          [assignmentId],
          (assignment: any) => {
            return assignment.set(
              "status",
              isAfter(
                new Date(),
                new Date(getAssignmentDueDate(assignments, assignmentId))
              )
                ? ReceivedAssignmentStatus.late
                : ReceivedAssignmentStatus.complete
            );
          }
        );
      }

      return assignments;
    }
    default:
      return assignments;
  }
}

function getUpdatedSectionStat(
  currentStat: any,
  { sessionScore, sessionDuration }: any
) {
  const {
    bestScore,
    worstScore,
    averageScore,
    firstScore,
    sessionsCompleted,
    totalStudyTime
  } = currentStat;
  return new SectionStat({
    bestScore: Math.max(bestScore, sessionScore),
    worstScore: Math.min(worstScore, sessionScore),
    averageScore:
      (averageScore * sessionsCompleted + sessionScore) /
      (sessionsCompleted + 1),
    lastScore: sessionScore,
    sessionsCompleted: sessionsCompleted + 1,
    totalStudyTime: totalStudyTime + sessionDuration,
    firstScore
  });
}

function getIsBestOrWorstQuizSession({
  assignmentId,
  assignments,
  quizId,
  newSessionModules
}: {
  assignments: any;
  assignmentId: string;
  quizId: string;
  newSessionModules: any[];
}) {
  const { prevBestSessionMarks, prevWorstSessionMarks } = assignments
    .getIn([assignmentId, "quizStats", quizId, "moduleStats"])
    .toList()
    .reduce(
      (
        acc: {
          prevBestSessionMarks: number;
          prevWorstSessionMarks: number;
        },
        moduleStat: any
      ) => ({
        prevBestSessionMarks: (acc.prevBestSessionMarks +=
          moduleStat.bestSessionMark),
        prevWorstSessionMarks: (acc.prevWorstSessionMarks +=
          moduleStat.worstSessionMarks)
      }),
      { prevBestSessionMarks: 0, prevWorstSessionMarks: 0 }
    );

  const newSessionMarks = newSessionModules.reduce(
    (acc, module) => (acc += module.sessionMark),
    0
  );

  return {
    isBestSession: newSessionMarks > prevBestSessionMarks,
    isWorstSession: newSessionMarks < prevWorstSessionMarks
  };
}

function getUpdatedQuizStat(
  currentStat: any,
  { sessionDuration, timestamp, modules, bestSession, worstSession }: any
) {
  const {
    totalStudyTime,
    sessionsCompleted,
    firstStudied,
    marksTotal,
    moduleStats
  } = currentStat;

  const updatedModuleStats = modules.reduce(
    (acc: any, { moduleId, sessionMark }: any) => {
      const currentModuleStat = moduleStats.get(moduleId);
      if (!currentModuleStat) return acc;

      return acc.set(
        moduleId,
        getUpdatedQuizModuleStat(currentModuleStat, {
          sessionMark,
          sessionsCompleted: sessionsCompleted + 1,
          bestSession,
          worstSession
        })
      );
    },
    Map()
  );

  return new QuizStat({
    firstStudied,
    marksTotal,
    totalStudyTime: totalStudyTime + sessionDuration,
    sessionsCompleted: sessionsCompleted + 1,
    lastStudied: timestamp,
    moduleStats: updatedModuleStats
  });
}

function getUpdatedQuizModuleStat(
  currentStat: any,
  { sessionMark, sessionsCompleted, bestSession, worstSession }: any
) {
  const {
    bestModuleMark,
    worstModuleMark,
    averageSessionMark,
    bestSessionMark,
    worstSessionMark,
    firstSessionMark,
    marksTotal
  } = currentStat;

  return new QuizModuleStat({
    firstSessionMark,
    marksTotal,
    bestModuleMark: Math.max(bestModuleMark, sessionMark),
    worstModuleMark: Math.min(worstModuleMark, sessionMark),
    averageSessionMark:
      (averageSessionMark * (sessionsCompleted - 1) + sessionMark) /
      sessionsCompleted,
    lastSessionMark: sessionMark,
    bestSessionMark: bestSession ? sessionMark : bestSessionMark,
    worstSessionMark: worstSession ? sessionMark : worstSessionMark
  });
}

function vocabularyAssignmentIsComplete(assignment: StudentAssignment) {
  const wordIds = getAssignmentSpecWordIds(assignment as any);
  const vocabularyStats = assignment.get("vocabularyStats") || Map();
  const completedWordIds = vocabularyStats
    .filter(stat => !!stat.get("sessionsCompleted"))
    .keySeq()
    .toSet();
  return wordIds.every(wordId => completedWordIds.has(wordId));
}

function assignmentIsComplete({
  state,
  assignmentId,
  sectionComplete = false,
  examQuestionComplete = false,
  quizComplete = false
}: {
  state: Map<string, Assignment>;
  assignmentId: string;
  sectionComplete?: boolean;
  examQuestionComplete?: boolean;
  quizComplete?: boolean;
}) {
  const assignment = getAssignment(state, assignmentId);
  if (!assignment) return false;

  const hasContentSpec = getHasContentSpec(assignment);
  const assignedSectionIds = getAssignmentSectionIds(assignment);
  const completedSectionIds = getStudiedSectionIds(assignment);

  const contentsOrSectionsAreCompleted = hasContentSpec
    ? allContentsCompleted(assignment)
    : Set(completedSectionIds).intersect(Set(assignedSectionIds)).size +
        (sectionComplete ? 1 : 0) ===
      assignedSectionIds.size;

  if (
    !assignmentIncludesExamQuestions(assignment) &&
    !assignmentIncludesQuizzes(assignment)
  )
    return contentsOrSectionsAreCompleted;

  const assignedQuestionIds = getAssignmentExamQuestionIds(assignment);
  if (assignedQuestionIds.size === 0 && !assignmentIncludesQuizzes(assignment))
    return contentsOrSectionsAreCompleted;

  const completedQuestionIds = getStudiedQuestionIds(assignment);
  const examQuestionsCompleted =
    Set(completedQuestionIds).intersect(Set(assignedQuestionIds)).size +
      (examQuestionComplete ? 1 : 0) ===
    assignedQuestionIds.size;

  const assignedQuizIds = getAssignmentQuizIds(assignment);
  if (assignedQuizIds.size === 0)
    return contentsOrSectionsAreCompleted && examQuestionsCompleted;

  const completedQuizIds = getStudiedQuizIds(assignment);
  const quizzesCompleted =
    Set(completedQuizIds).intersect(Set(assignedQuizIds)).size +
      (quizComplete ? 1 : 0) ===
    assignedQuizIds.size;

  return (
    contentsOrSectionsAreCompleted && examQuestionsCompleted && quizzesCompleted
  );
}
